import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Input, Dropdown, Modal, App } from 'antd';

import { api } from '../../api/client';
import { API_CARD_DEFS_URL, isAdmin } from '../../utils';
import { KEBAB, MAGNIFYING_GLASS, PLUS, RATING_STAR_FILL, TAG_ICON, CARD_ICON } from '../icons';
import EmptyState from '../common/EmptyState';
import { NO_CARDS_COPY } from './NoCardsEmptyState';
import { normalizeId } from '../../utils/ids';
import { cardPagePath, libraryCardPath } from '../../utils/cards';
import RecordBadge, { RecordIdentity, RecordTitle } from '../common/RecordBadge';
import { cardOwnerOf } from '../cards/CardVisuals';
import GroupHeader from '../common/GroupHeader';
import { buildActionMenu } from '../common/actionMenu';
import ShareModal from '../common/ShareModal';
import TempShareLinkSection from '../common/TempShareLinkSection';
import { buildCardShareUrl } from '../../utils/shareUrls';
import { slugify } from '../../utils/strings';
import { getCollectionOwnCardIds } from '../../utils/collections';
import { inheritedCardIdsOf, parentOf, subCollectionsOf } from '../../utils/collectionFamily';
import { canAddSubCollection } from '../../utils/familyActions';
import CardMoveModal from './CardMoveModal';
import NewSubCollectionModal from './NewSubCollectionModal';
import { canAddToCollection, cardScopeLabel, getCardScope, isOwnedByCollection, isRetiredCard } from '../../utils/cardScopes';
import { useCardDefinitions } from '../../contexts/CardDefinitionsContext';
import ElementSetList from '../elementSets/ElementSetList';
import { canManage } from '../../utils/permissions';
import { collectionCardsPath, newCardPath } from '../../config/collectionShell';
import GlobalLiveNotice from './GlobalLiveNotice';
import ConfirmWordModal from '../common/ConfirmWordModal';
import { confirmDestructive } from '../common/confirmDestructive';
import ItemTagModeModal from './ItemTagModeModal';
import { itemTagSettingFor, itemTagSettingPhrase, itemTagsOn, ownItemTagMode } from '../../utils/itemTagSettings';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';
import TabLoadState, { RefreshFailedNotice } from '../common/TabLoadState';
import useCollectionPackages from '../../hooks/useCollectionPackages';
import { elementSetBundles, groupBundleRows } from '../../utils/bundleGroups';
import BundleGroup from './BundleGroup';
import BundleAddOnDialog from './BundleAddOnDialog';
import RemoveBundleDialog from './RemoveBundleDialog';
import DetachBundleDialog from './DetachBundleDialog';
import ReviewUpdateDialog from './ReviewUpdateDialog';
import { bundleGroupActions } from './bundleGroupActions';

/**
 * Card Library — the manager's view of which cards this collection offers.
 *
 * The page reads top to bottom: a toolbar (the search, the one filled primary
 * Add card, the labeled secondary Create a card, and a ⋯ for the other doors),
 * then the cards already in the collection (where collection-level config
 * lives). Adding is a picker, opened by Add card, over the global card list
 * (KPR-351, owner ruling d10). The search term lives in the address as ?q= and
 * narrows both the member list and the picker. A collection with no cards
 * shows one empty state with the one button that does the next step, or who to
 * ask; "No matches" is a different state from empty.
 *
 * Membership has two sources (mirroring the API): the embedded
 * collection.cards[] refs, plus every card definition OWNED by this collection
 * (scope 'collection'). Owned cards are members by definition — they can't be
 * removed, only deleted (or promoted to global by an admin). Global cards are
 * members only while an embedded ref exists, so they get Remove.
 *
 * The collection favorite set here is manager-owned and applies to every user of
 * the collection; it rides along in collection.cards[].favorite and saves with
 * the rest of the form. Per-user favorites are a separate thing entirely and
 * live in FavoritesContext.
 *
 * USE ITEMS AS TAGS (docs/plans/tags.md §3.2, §5.2; build step T4d) sits
 * beside them, for a manager of a saved collection: Use items as tags… opens
 * ItemTagModeModal — Off · Chosen items · Every item, and Apply where
 * referenced. Unlike the pin it is NOT a form change: it
 * writes at once through its own endpoint (the collection PUT refuses to
 * change it), and `onItemTagsChanged` tells the form to re-read the
 * collection, whose `cards[]` the endpoint just moved. The row's subtitle
 * says the setting, and where it comes from when a card it extends set it;
 * a card whose items are tags cannot be removed until it is turned off.
 *
 * BUNDLE GROUPS (KPR-1130, epic KPE-035): a Discover bundle installed here
 * draws its cards as one group between *In this collection* and *From
 * {parent}*, with the bundle's name, publisher, version and (for a manager)
 * its update state, and a ⋯ of the bundle's own actions (`bundleGroupActions`).
 * Which cards belong to which bundle is the API's install record
 * (`useCollectionPackages`); *In this collection* keeps the cards no record
 * names, and a card stamped by a package whose record is not there yet stays
 * there too. The element sets a bundle brought stay in their own list below,
 * each with a "From <bundle>" line.
 *
 * THE ROW (KPR-339, owner ruling d09): both live in the row's ⋯, in
 * its Record group, like every other row action; the row itself keeps the
 * name, the line under it, a small glyph per setting that is on, and the ⋯
 * (44px on a touch screen). The glyphs say nothing the line does not say
 * in words, so they are hidden from screen readers.
 *
 * READERS (KPR-1281, KPE-039): the library is the collection's Cards tab,
 * which everyone who reads the collection opens. A reader sees every list
 * a manager sees (the cards, bundle groups, From {parent}, In {sub}, element
 * sets) and can open and share a card; every building action (Add card,
 * Create a card, Create an element set, New sub-collection, and a row's
 * edits: Edit, pin, Use items as tags, Extend, Move, Promote, Delete,
 * Remove) is left out, never shown disabled.
 */
export default function CardLibrary( props ) {

	// `onReload`: re-read the collection after a move changed it on the
	// server (its cards and their flags travelled).
	// `onCardsChangedOnServer`: a delete or promote already wrote through its
	// own endpoint, so the form re-baselines instead of calling it unsaved.
	const { collection, setCollection, onReload, onItemTagsChanged, onCardsChangedOnServer } = props;
	const navigate = useNavigate();
	const { message, modal } = App.useApp();
	// One source of truth for definitions: the app-wide cache. Mutations here
	// (delete/promote) refresh it for every consumer at once.
	const { cards: cardDefs, loading, loaded: cardsLoaded, error: cardsError, refresh: refreshCardDefinitions } = useCardDefinitions();
	// The cards read failed before it ever answered: say so with Retry, never "no cards" (KPR-728).
	const cardsFailed = Boolean( cardsError ) && ! cardsLoaded;
	const cardsRefreshFailed = Boolean( cardsError ) && cardsLoaded;

	// The search term lives in the address as ?q= (reload, sharing, Back), like
	// the lists. It is a plain term here, not KQL: it narrows the cards of this
	// collection and the cards that can be added, both in memory.
	const [ searchParams, setSearchParams ] = useSearchParams();
	const search = searchParams.get( 'q' ) || '';
	const setSearch = useCallback( ( value ) => {
		setSearchParams( prev => {
			const next = new URLSearchParams( prev );
			if ( value ) next.set( 'q', value ); else next.delete( 'q' );
			return next;
		}, { replace: true } );
	}, [ setSearchParams ] );
	// The Add card picker (one primary button opens it).
	const [ pickerOpen, setPickerOpen ] = useState( false );
	// The member row whose share modal is open, if any. This is the ONE card
	// surface with a collection in hand, which is what a card share link needs
	// (a card is shared in the context of a collection it belongs to).
	const [ shareRow, setShareRow ] = useState( null );
	// The owned card whose delete is being confirmed — the card page's Danger
	// zone dialog (CardDangerZone), armed on the typed word, from this door too.
	const [ deletingRow, setDeletingRow ] = useState( null );
	const [ deleteBusy, setDeleteBusy ] = useState( false );
	// SUB-COLLECTIONS (docs/plans/sub-collections.md §5.6): the card being
	// moved along the family line, the new sub-collection modal, and which
	// sub-collection groups are open (collapsed by default).
	const [ movingRow, setMovingRow ] = useState( null );
	// The card whose "Use items as tags" window is open (T4d).
	const [ itemTagRow, setItemTagRow ] = useState( null );
	const [ newSubOpen, setNewSubOpen ] = useState( false );
	const [ openGroups, setOpenGroups ] = useState( () => new Set() );
	// Bundle groups are open by default, so it is the CLOSED ones we keep; and
	// the bundle whose Add an add-on dialog is open, if any.
	const [ closedBundles, setClosedBundles ] = useState( () => new Set() );
	const [ addOnFor, setAddOnFor ] = useState( null );
	const [ removeFor, setRemoveFor ] = useState( null );
	const [ detachFor, setDetachFor ] = useState( null );
	// The bundle whose update is being reviewed (keepr-api KPR-458).
	const [ updateFor, setUpdateFor ] = useState( null );
	const parent = parentOf( collection );
	const subCollections = useMemo( () => subCollectionsOf( collection ), [ collection ] );
	const inheritedIds = useMemo( () => inheritedCardIdsOf( collection ), [ collection ] );

	// Unsaved collections have no id yet: cards can't be created for (or owned
	// by) them until the first save.
	const collectionId = normalizeId( collection?._id );
	const admin = isAdmin();
	// A manager can act; so can the person making a collection that has no id
	// yet. Anyone else reads (KPR-1281): the building actions are left out.
	const canAct = ! collectionId || canManage( collection );
	// The bundles this collection holds (KPR-1130). Until the first answer the
	// library waits, so a card is never drawn ungrouped and then moved; a
	// refused or failed read lists everything flat, as before.
	const { packages, loaded: packagesLoaded, error: packagesError, refresh: refreshPackages } = useCollectionPackages( collectionId );
	const packagesPending = Boolean( collectionId ) && ! packagesLoaded && ! packagesError;
	const setBundles = useMemo( () => elementSetBundles( packages ), [ packages ] );
	// Update and the bundle's actions are a manager's, and not in an archived
	// collection, which takes no change.
	const bundlesActionable = Boolean( collectionId ) && canManage( collection ) && collection?.status !== 'archived';

	const defsById = useMemo( () => {
		const map = new Map();
		( cardDefs || [] ).forEach( cd => {
			const id = normalizeId( cd?._id );
			if ( id ) map.set( id, cd );
		} );
		return map;
	}, [ cardDefs ] );

	// Favorites first, then by name — the same order the picker pins them in.
	// Membership union comes from the shared two-source helper; refs are joined
	// back on for the per-card config (favorite).
	const memberRows = useMemo( () => {
		const memberRefs = Array.isArray( collection?.cards ) ? collection.cards : [];
		const refById = new Map();
		memberRefs.forEach( ref => {
			const id = normalizeId( ref?.cardDefinitionId );
			if ( id ) refById.set( id, ref );
		} );

		// A local ref to a card owned ABOVE (a sub-collection's star on an
		// inherited card) is not one of its own cards: those rows are the
		// From {parent} group below.
		return [ ...getCollectionOwnCardIds( collection, cardDefs ) ]
			.filter( id => ! inheritedIds.has( id ) )
			.map( id => {
				const ref = refById.get( id ) || null;
				const def = defsById.get( id );
				return {
					id,
					ref,
					def,
					name: def?.name || ref?.name || '(untitled card)',
					favorite: !! ref?.favorite,
					ownItemTags: ownItemTagMode( ref ),
					itemTags: itemTagSettingFor( collection, id, defsById ),
					owned: isOwnedByCollection( def, collectionId ),
					scope: getCardScope( def ),
				};
			} )
			.sort( ( a, b ) => {
				if ( a.favorite !== b.favorite ) return a.favorite ? -1 : 1;
				return a.name.localeCompare( b.name );
			} );
	}, [ collection, cardDefs, defsById, collectionId, inheritedIds ] );

	// A sub-collection's cards from above: read-only here but for the star,
	// which writes this collection's own ref (§2.3).
	const inheritedRows = useMemo( () => {
		if ( ! inheritedIds.size ) return [];
		const refs = Array.isArray( collection?.cards ) ? collection.cards : [];
		return [ ...inheritedIds ].map( id => {
			const ref = refs.find( r => normalizeId( r?.cardDefinitionId ) === id ) || null;
			const def = defsById.get( id );
			return {
				id,
				ref,
				def,
				name: def?.name || ref?.name || '(untitled card)',
				favorite: !! ref?.favorite,
				ownItemTags: ownItemTagMode( ref ),
				itemTags: itemTagSettingFor( collection, id, defsById ),
				owned: false,
				inherited: true,
				scope: getCardScope( def ),
			};
		} ).sort( ( a, b ) => a.name.localeCompare( b.name ) );
	}, [ inheritedIds, collection, defsById ] );

	// A parent's sub-collections, each with its own cards (doors only here).
	const familyGroups = useMemo( () => subCollections.map( sub => ( {
		sub,
		cards: [ ...sub.cardIds ].map( id => defsById.get( id ) ).filter( Boolean ).sort( ( a, b ) => ( a.name || '' ).localeCompare( b.name || '' ) ),
	} ) ), [ subCollections, defsById ] );

	// Where a card of this collection may move: down into a sub-collection,
	// or — from a sub-collection — up into its parent.
	const moveTargets = useMemo( () => {
		if ( subCollections.length ) return subCollections.map( sub => ( { ...sub, direction: 'down' } ) );
		if ( parent ) return [ { ...parent, direction: 'up' } ];
		return [];
	}, [ subCollections, parent ] );

	const memberIds = useMemo(
		() => new Set( [ ...memberRows.map( row => row.id ), ...inheritedIds ] ),
		[ memberRows, inheritedIds ]
	);

	const addCard = useCallback( ( cd ) => {
		const normalized = normalizeId( cd?._id );
		if ( ! normalized ) return;
		setCollection( prev => {
			const cards = Array.isArray( prev?.cards ) ? prev.cards : [];
			if ( cards.some( c => normalizeId( c?.cardDefinitionId ) === normalized ) ) return prev;
			return { ...prev, cards: [ ...cards, { cardDefinitionId: normalized, name: cd.name } ] };
		} );
	}, [ setCollection ] );

	const removeCard = useCallback( ( id ) => {
		const normalized = normalizeId( id );
		if ( ! normalized ) return;
		setCollection( prev => {
			const cards = Array.isArray( prev?.cards ) ? prev.cards : [];
			const next = cards.filter( c => normalizeId( c?.cardDefinitionId ) !== normalized );
			if ( next.length === cards.length ) return prev;
			return { ...prev, cards: next };
		} );
	}, [ setCollection ] );

	// Toggle a per-entry config flag on collection.cards[], creating the entry
	// when it doesn't exist yet (owned cards are members without one until a
	// flag is set). Saved by the form's Save button with everything else.
	const toggleCardFlag = useCallback( ( row, flag ) => {
		setCollection( prev => {
			const cards = Array.isArray( prev?.cards ) ? prev.cards : [];
			const exists = cards.some( c => normalizeId( c?.cardDefinitionId ) === row.id );
			if ( ! exists ) {
				return { ...prev, cards: [ ...cards, { cardDefinitionId: row.id, name: row.name, [ flag ]: true } ] };
			}
			return {
				...prev,
				cards: cards.map( c => (
					normalizeId( c?.cardDefinitionId ) === row.id
						? { ...c, [ flag ]: ! c?.[ flag ] }
						: c
				) ),
			};
		} );
	}, [ setCollection ] );

	// Delete a collection-owned card definition. Immediate (not part of the
	// form save) — soft-deleted server-side, restorable from Recently deleted. The API
	// also removes the embedded membership entry; removeCard mirrors that in
	// the local form state. Arms on the typed word, as on the card page.
	const deleteOwnedCard = useCallback( ( row ) => setDeletingRow( row ), [] );

	const confirmDeleteOwnedCard = useCallback( async () => {
		const row = deletingRow;
		if ( ! row ) return;
		setDeleteBusy( true );
		try {
			await api.delete( `${ API_CARD_DEFS_URL }/${ row.id }` );
			removeCard( row.id );
			message.success( 'Card deleted.' );
			setDeletingRow( null );
			refreshCardDefinitions();
			refreshPackages();
			onCardsChangedOnServer?.();
		}
		catch ( err ) {
			message.error( err?.response?.data?.message || 'Delete failed.' );
		}
		finally {
			setDeleteBusy( false );
		}
	}, [ deletingRow, removeCard, message, refreshCardDefinitions, refreshPackages, onCardsChangedOnServer ] );

	// Remove a global card from this collection: a form change like the
	// others here, so nothing is sent until Save — but it is still asked.
	const confirmRemoveCard = useCallback( ( row ) => {
		confirmDestructive( modal, {
			title: `Remove the "${ row.name }" card from this collection?`,
			// A retired card (KPR-280) cannot be added back once the removal is
			// saved: keepr no longer offers it to collections that do not use it.
			content: isRetiredCard( row.def )
				? 'No new items here can use it. Items already made from it keep their data. This card is retired, so once you save, it cannot be added back. The change takes effect when you save the collection.'
				: 'No new items here can use it. Items already made from it keep their data. The change takes effect when you save the collection.',
			okText: 'Remove',
			onOk: () => removeCard( row.id ),
		} );
	}, [ modal, removeCard ] );

	// Admin-only: collection-owned -> global. The server adds the embedded
	// membership entry; mirror it locally so a later form Save (whole-doc PUT)
	// doesn't drop the entry the server just added.
	const promoteCard = useCallback( ( row ) => {
		Modal.confirm( {
			title: `Promote "${ row.name }" to a global card?`,
			content: 'Every keepr user will be able to use it in their collections. This collection keeps using it, but its managers can no longer edit it. This cannot be undone from the app.',
			okText: 'Promote',
			onOk: async () => {
				try {
					await api.post( `${ API_CARD_DEFS_URL }/${ row.id }/promote` );
					if ( ! row.ref ) addCard( { _id: row.id, name: row.name } );
					message.success( 'Card promoted to global.' );
					refreshCardDefinitions();
					onCardsChangedOnServer?.();
				}
				catch ( err ) {
					message.error( err?.response?.data?.message || 'Promote failed.' );
				}
			},
		} );
	}, [ addCard, message, refreshCardDefinitions, onCardsChangedOnServer ] );

	// The platform taxonomy: navigate · record · share · destructive. The
	// row's own settings lead the Record group: the pin is a form change
	// (saved with Save), Use items as tags… opens its window.
	const rowMenu = useCallback( ( row ) => buildActionMenu( [
		{
			key: 'open',
			group: 'navigate',
			glyph: 'open',
			label: 'Open card definition',
			onClick: () => navigate( libraryCardPath( row, collection ) ),
		},
		// The card's home is where "open" lands; "edit" is the deep link to
		// the first editing tab. A global card is keepr's to edit.
		( canAct && ( row.scope !== 'global' || admin ) ) ? {
			key: 'edit',
			group: 'navigate',
			glyph: 'edit',
			label: 'Edit card',
			onClick: () => navigate( libraryCardPath( row, collection, 'tab=basics' ) ),
		} : null,
		// The pin writes this collection's own ref, on its own cards and on
		// a sub-collection's cards from above alike.
		canAct ? {
			key: 'favorite',
			group: 'record',
			glyph: row.favorite ? 'favoriteOn' : 'favorite',
			label: row.favorite ? 'Unpin for everyone' : 'Pin for everyone',
			title: row.favorite
				? 'Take it off the top of the card picker for everyone'
				: 'Put it at the top of the card picker for everyone using this collection',
			onClick: () => toggleCardFlag( row, 'favorite' ),
		} : null,
		( collectionId && canManage( collection ) ) ? {
			key: 'item-tags',
			group: 'record',
			glyph: 'tag',
			label: 'Use items as tags…',
			title: 'Whether this card’s items can be put on other items as tags. It applies at once.',
			onClick: () => setItemTagRow( row ),
		} : null,
		// A card from above is extended here as this collection's own card
		// (a sub-collection's card may extend one owned above it).
		// Not a RETIRED card (KPR-280): a new card built on it is a new
		// adopter, which the API refuses (400 card_retired).
		( canAct && ( row.scope === 'global' || row.inherited ) && collectionId && ! isRetiredCard( row.def ) ) ? {
			key: 'extend',
			group: 'record',
			glyph: 'duplicate',
			label: 'Extend into a new card',
			onClick: () => navigate( newCardPath( collectionId, { parentCardId: row.id } ) ),
		} : null,
		( row.owned && moveTargets.length && canManage( collection ) ) ? {
			key: 'move',
			group: 'record',
			glyph: 'collection',
			label: subCollections.length ? 'Move to sub-collection…' : `Move up to ${ parent?.name || 'the parent collection' }…`,
			onClick: () => setMovingRow( row ),
		} : null,
		( canAct && row.owned && admin ) ? {
			key: 'promote',
			group: 'record',
			glyph: 'collection',
			label: 'Promote to global',
			onClick: () => promoteCard( row ),
		} : null,
		( collectionId && ! row.inherited ) ? {
			key: 'share',
			group: 'share',
			glyph: 'share',
			label: 'Share card',
			onClick: () => setShareRow( row ),
		} : null,
		( canAct && row.owned ) ? {
			key: 'delete',
			group: 'destructive',
			glyph: 'delete',
			label: 'Delete card',
			onClick: () => deleteOwnedCard( row ),
		} : null,
		( canAct && ! row.owned && row.ref ) ? {
			key: 'remove',
			group: 'destructive',
			glyph: 'remove',
			label: 'Remove from this collection',
			// Its items are tags here: the API refuses dropping the entry
			// (409 item_tags_on) until Use items as tags is turned off.
			disabled: itemTagsOn( row.ownItemTags ),
			title: itemTagsOn( row.ownItemTags ) ? 'Its items are used as tags here. Turn Use items as tags off for it first.' : undefined,
			onClick: () => confirmRemoveCard( row ),
		} : null,
	] ), [ admin, canAct, collectionId, navigate, promoteCard, deleteOwnedCard, confirmRemoveCard, moveTargets, subCollections, parent, collection, toggleCardFlag ] );

	const rowSubtitle = ( row ) => {
		const parts = [];
		if ( row.inherited ) parts.push( `From ${ parent?.name || 'the parent collection' }` );
		else if ( row.owned ) parts.push( 'Owned by this collection' );
		else {
			const scope = cardScopeLabel( row.def, collectionId );
			if ( scope ) parts.push( `${ scope } card` );
		}
		// A retired global card (KPR-280) still works here; it is no longer
		// offered to collections that do not hold it.
		if ( isRetiredCard( row.def ) ) parts.push( 'Retired' );
		if ( row.favorite ) parts.push( 'Pinned for everyone' );
		const tagged = itemTagSettingPhrase( row.itemTags, {
			cardId: row.id,
			cardName: defsById.get( row.itemTags?.fromCardId )?.name,
		} );
		if ( tagged ) parts.push( tagged );
		return parts.length ? parts.join( ' · ' ) : null;
	};

	const normalizedSearch = search.trim().toLowerCase();
	const matchesSearch = ( ...parts ) => ! normalizedSearch
		|| parts.join( ' ' ).toLowerCase().includes( normalizedSearch );

	// Only global (and pre-migration legacy) cards are addable — a card owned
	// by another collection is exclusive to it, and this collection's own
	// cards are members automatically. A RETIRED global card (KPR-280, ruling
	// G14) has left the library: the API refuses it as a new member.
	const available = useMemo(
		() => ( cardDefs || [] ).filter( cd => {
			const id = normalizeId( cd?._id );
			return id && ! memberIds.has( id ) && canAddToCollection( cd, collectionId ) && ! isRetiredCard( cd );
		} ),
		[ cardDefs, memberIds, collectionId ]
	);

	const searchResults = useMemo( () => available.filter( cd => (
		matchesSearch( cd?.name || '', cd?.description || '' )
	) ), [ available, normalizedSearch ] ); // eslint-disable-line react-hooks/exhaustive-deps

	const shownMemberRows = useMemo( () => memberRows.filter( row => matchesSearch( row.name, row.def?.description || '' ) ),
		[ memberRows, normalizedSearch ] ); // eslint-disable-line react-hooks/exhaustive-deps
	const shownInheritedRows = useMemo( () => inheritedRows.filter( row => matchesSearch( row.name, row.def?.description || '' ) ),
		[ inheritedRows, normalizedSearch ] ); // eslint-disable-line react-hooks/exhaustive-deps

	// Bundle groups: the cards the install record names are lifted out of
	// *In this collection* into their bundle's group. The search narrows the
	// cards inside every group, and a group left with none is hidden.
	const grouping = useMemo( () => groupBundleRows( packages, memberRows ), [ packages, memberRows ] );
	const ungroupedRows = useMemo( () => memberRows.filter( row => ! grouping.grouped.has( row.id ) ), [ memberRows, grouping ] );
	const shownUngroupedRows = useMemo( () => ungroupedRows.filter( row => matchesSearch( row.name, row.def?.description || '' ) ),
		[ ungroupedRows, normalizedSearch ] ); // eslint-disable-line react-hooks/exhaustive-deps
	const shownGroups = useMemo( () => groupBundleRows( packages, shownMemberRows ).groups, [ packages, shownMemberRows ] );
	const hasGroups = grouping.groups.length > 0;

	// Nothing at all, before any search: the collection is truly empty, which
	// is a different state from "nothing matches" (KPR-351).
	const libraryEmpty = cardsLoaded && memberRows.length === 0 && inheritedRows.length === 0;
	// *In this collection* stands aside when every card is in a bundle's group
	// (nothing of its own to list, and no search is looking for one).
	const searching = normalizedSearch !== '';
	const showOwnSection = ! hasGroups || ! cardsLoaded || packagesPending || libraryEmpty
		|| ( searching ? ( shownUngroupedRows.length > 0 || shownGroups.length === 0 ) : ungroupedRows.length > 0 );

	const clearSearch = () => setSearch( '' );
	const noMatches = ( what ) => (
		<div className='card-library__empty' data-testid='card-library-no-matches'>
			{ `No ${ what } match “${ search.trim() }”. ` }
			<Button type='link' size='small' onClick={clearSearch}>Clear search</Button>
		</div>
	);

	// The page's other doors, behind one ⋯ in the taxonomy's order.
	const moreMenu = buildActionMenu( [
		{ key: 'discover', group: 'navigate', glyph: 'open', label: 'Browse Discover', onClick: () => navigate( '/discover' ) },
		canAct ? {
			key: 'element-set',
			group: 'record',
			glyph: 'card',
			label: 'Create an element set',
			disabled: ! collectionId,
			title: collectionId ? undefined : 'Save the collection first: new element sets belong to it',
			onClick: () => navigate( `/element-sets/new?collectionId=${ collectionId }` ),
		} : null,
		( canAct && canAddSubCollection( collection ) ) ? {
			key: 'sub-collection',
			group: 'record',
			glyph: 'collection',
			label: 'New sub-collection…',
			onClick: () => setNewSubOpen( true ),
		} : null,
	] );

	// One member row: the plain card title, the line under it, a glyph per
	// setting that is on, and the row ⋯ that holds every action (KPR-339).
	const renderMemberRow = row => {
		const subtitle = rowSubtitle( row );
		const tagsOn = itemTagsOn( row.itemTags?.mode );
		return (
			<RecordIdentity
				key={row.id}
				className='card-library__row'
				badge={(
					<RecordTitle
						kind='card'
						record={row.def || { name: row.name }}
						title={row.name}
						wrap
						// A card this collection uses but another owns (a
						// parent's) is qualified by its owner.
						collection={row.def ? cardOwnerOf( row.def ) : null}
					/>
				)}
				below={subtitle ? <span>{subtitle}</span> : null}
				actions={(
					<>
						{ ( row.favorite || tagsOn ) ? (
							<span className='card-library__states' aria-hidden='true'>
								{ row.favorite ? (
									<span className='card-library__state' data-state='favorite' title='Pinned for everyone'>
										<RATING_STAR_FILL />
									</span>
								) : null }
								{ tagsOn ? (
									<span className='card-library__state' data-state='item-tags' title={itemTagSettingPhrase( row.itemTags, { cardId: row.id, cardName: defsById.get( row.itemTags?.fromCardId )?.name } ) || undefined}>
										<TAG_ICON />
									</span>
								) : null }
							</span>
						) : null }
						<Dropdown trigger={[ 'click' ]} placement='bottomRight' menu={rowMenu( row )}>
							<Button
								type='text'
								className='card-library__action'
								aria-label={`${ row.name } actions`}
								icon={<KEBAB className='medium-icon' />}
							/>
						</Dropdown>
					</>
				)}
			/>
		);
	};

	return <>
		<GlobalLiveNotice collection={collection} strong className='card-library__alert' />
		<div className='form-bundle'>

			{/* One filled primary: Add card opens the picker. Create a card is
			    the one labeled secondary; the other doors sit behind the ⋯.
			    A collection with no cards says the next step in its own empty
			    state instead, so the page never shows two. */}
			<div className='card-library__toolbar'>
				<div className='card-library__search'>
					<Input
						{...NO_TEXT_ASSIST}
						allowClear
						prefix={<MAGNIFYING_GLASS aria-hidden='true' />}
						placeholder='Search cards…'
						aria-label='Search cards'
						value={search}
						onChange={e => setSearch( e.target.value )}
						onKeyDown={e => { if ( e.key === 'Escape' && search ) { e.preventDefault(); e.stopPropagation(); clearSearch(); } }}
					/>
				</div>
				<div className='card-library__toolbar-actions'>
					{ canAct && ! libraryEmpty ? (
						<Button type='primary' icon={<PLUS />} disabled={! cardsLoaded} onClick={() => setPickerOpen( true )}>
							Add card
						</Button>
					) : null }
					{ canAct ? (
						<Button
							icon={<PLUS />}
							disabled={! collectionId}
							title={collectionId ? undefined : 'Save the collection first: new cards belong to it'}
							onClick={() => navigate( newCardPath( collectionId ) )}
						>
							Create a card
						</Button>
					) : null }
					<Dropdown trigger={[ 'click' ]} placement='bottomRight' menu={moreMenu}>
						<Button className='card-library__action' aria-label='More card library actions' icon={<KEBAB className='medium-icon' />} />
					</Dropdown>
				</div>
			</div>

			{ showOwnSection ? (
				<div className='card-library__section'>
					<GroupHeader as='h4' label='In this collection' count={shownUngroupedRows.length} countLabel='card' />

					{ cardsRefreshFailed ? <RefreshFailedNotice label='cards' onRetry={refreshCardDefinitions} /> : null }
					{ packagesError ? <RefreshFailedNotice label='bundle groups' onRetry={refreshPackages} /> : null }

					{ ! cardsLoaded && ( loading || cardsFailed ) ? (
						<TabLoadState loading={loading} error={cardsError} label='cards' onRetry={refreshCardDefinitions} rows={2} />
					) : packagesPending ? (
						<TabLoadState loading label='cards' rows={2} />
					) : libraryEmpty ? (
						canAct ? (
							<EmptyState
								className='card-library__empty-state'
								data-testid='card-library-empty'
								icon={<CARD_ICON />}
								title='No cards in this collection yet'
								description={ available.length || ! collectionId
									? 'Add a card from the library, or create your own.'
									: NO_CARDS_COPY.managerNext }
								primaryAction={ available.length || ! collectionId
									? { label: 'Add card', icon: <PLUS />, onClick: () => setPickerOpen( true ) }
									: { label: 'Create a card', icon: <PLUS />, to: newCardPath( collectionId ) } }
							/>
						) : (
							<EmptyState
								className='card-library__empty-state'
								data-testid='card-library-empty'
								icon={<CARD_ICON />}
								title={NO_CARDS_COPY.readerTitle}
								description={NO_CARDS_COPY.whatACardIs}
								note={NO_CARDS_COPY.readerNote}
							/>
						)
					) : shownUngroupedRows.length ? (
						<div className='card-library__grid'>
							{ shownUngroupedRows.map( renderMemberRow ) }
						</div>
					) : ( memberRows.length > 0 && searching && shownGroups.length === 0 ) ? (
						noMatches( 'cards in this collection' )
					) : null }
				</div>
			) : null }

			{/* Each Discover bundle installed here: its cards together, under
			    its name, with the bundle's own actions (KPR-1130). */}
			{ cardsLoaded && ! packagesPending ? shownGroups.map( group => (
				<BundleGroup
					key={group.bundle.key}
					group={group}
					open={! closedBundles.has( group.bundle.key )}
					onToggle={() => setClosedBundles( prev => {
						const next = new Set( prev );
						if ( next.has( group.bundle.key ) ) next.delete( group.bundle.key ); else next.add( group.bundle.key );
						return next;
					} )}
					menu={buildActionMenu( bundleGroupActions( {
						actionable: bundlesActionable,
						bundle: group.bundle,
						collectionId,
						navigate,
						onAddAddOn: setAddOnFor,
						onRemoveBundle: setRemoveFor,
						onDetachBundle: setDetachFor,
						onReviewUpdate: setUpdateFor,
					} ) )}
					renderRow={renderMemberRow}
					showUpdate={bundlesActionable}
				/>
			) ) : null }

			{/* A sub-collection's cards from above: usable here like its own,
			    changed where they are defined. Pin for everyone is this
			    collection's own setting for them. */}
			{ shownInheritedRows.length ? (
				<div className='card-library__section' data-testid='card-library-inherited'>
					<GroupHeader
						as='h4'
						label={`From ${ parent?.name || 'the parent collection' }`}
						count={shownInheritedRows.length}
						countLabel='card'
					/>
					<p className='card-library__hint'>
						These cards are defined on { parent?.name || 'the parent collection' } and can be used here.
						Change them there. Pinning one for everyone is this collection&rsquo;s own setting.
					</p>
					<div className='card-library__grid'>
						{ shownInheritedRows.map( renderMemberRow ) }
					</div>
				</div>
			) : null }

			{/* A parent's sub-collections: each one's own cards, as doors. */}
			{ familyGroups.map( ( { sub, cards } ) => {
				const open = openGroups.has( sub._id );
				return (
					<div key={sub._id} className='card-library__section card-library__family' data-testid='card-library-sub-collection'>
						<GroupHeader
							as='h4'
							label={`In ${ sub.name }`}
							count={cards.length}
							countLabel='card'
							collapsible
							collapsed={! open}
							onToggle={() => setOpenGroups( prev => {
								const next = new Set( prev );
								if ( next.has( sub._id ) ) next.delete( sub._id ); else next.add( sub._id );
								return next;
							} )}
						/>
						{ open ? (
							<>
								<div className='card-library__family-doors'>
									<Button type='link' size='small' className='card-library__link-action' onClick={() => navigate( collectionCardsPath( sub._id ) )}>
										{ `Open ${ sub.name }'s library` }
									</Button>
								</div>
								{ cards.length ? (
									<div className='card-library__grid'>
										{ cards.map( card => (
											<RecordIdentity
												key={normalizeId( card._id )}
												className='card-library__row'
												badge={<RecordTitle kind='card' record={card} title={card.name || '(untitled card)'} to={cardPagePath( card )} wrap />}
												below={<span>{ `Owned by ${ sub.name }` }</span>}
												actions={(
													<Dropdown
														trigger={[ 'click' ]}
														placement='bottomRight'
														menu={buildActionMenu( [
															{ key: 'open', group: 'navigate', glyph: 'open', label: 'Open card', onClick: () => navigate( cardPagePath( card ) ) },
														] )}
													>
														<Button type='text' className='card-library__action' aria-label={`${ card.name || 'Card' } actions`} icon={<KEBAB className='medium-icon' />} />
													</Dropdown>
												)}
											/>
										) ) }
									</div>
								) : (
									<div className='card-library__empty'>{ `${ sub.name } has no cards of its own; it uses this collection's. Move a card down from its ⋯ menu to give it one.` }</div>
								) }
							</>
						) : null }
					</div>
				);
			} ) }

			{/* The element sets a card of this collection may attach: the
			    global ones and its own (docs/plans/element-sets-and-me.md).
			    Unsaved collections own nothing yet. */}
			{ collectionId ? (
				<ElementSetList collectionId={collectionId} canManage={admin || canManage( collection )} bundleOf={setBundles} />
			) : null }

		</div>

		{ addOnFor ? (
			<BundleAddOnDialog
				bundleKey={addOnFor.key}
				bundleName={addOnFor.name}
				collectionId={collectionId}
				onClose={() => setAddOnFor( null )}
			/>
		) : null }

		{ removeFor ? (
			<RemoveBundleDialog
				bundleKey={removeFor.key}
				bundleName={removeFor.name}
				collectionId={collectionId}
				onClose={() => setRemoveFor( null )}
				onRemoved={() => { refreshCardDefinitions(); refreshPackages(); onCardsChangedOnServer?.(); }}
			/>
		) : null }

		{ updateFor ? (
			<ReviewUpdateDialog
				bundleKey={updateFor.key}
				bundleName={updateFor.name}
				collectionId={collectionId}
				onClose={() => setUpdateFor( null )}
				onChanged={() => { refreshCardDefinitions(); refreshPackages(); onCardsChangedOnServer?.(); }}
			/>
		) : null }

		{ detachFor ? (
			<DetachBundleDialog
				bundleKey={detachFor.key}
				bundleName={detachFor.name}
				collectionId={collectionId}
				onClose={() => setDetachFor( null )}
				onDetached={() => { refreshCardDefinitions(); refreshPackages(); onCardsChangedOnServer?.(); }}
			/>
		) : null }

		{ movingRow ? (
			<CardMoveModal
				open
				onClose={() => setMovingRow( null )}
				card={movingRow}
				sourceId={collectionId}
				targets={moveTargets}
				onMoved={() => onReload?.()}
			/>
		) : null }

		{ itemTagRow ? (
			<ItemTagModeModal
				open
				onClose={() => setItemTagRow( null )}
				collectionId={collectionId}
				collectionName={collection?.name || null}
				card={{ id: itemTagRow.id, name: itemTagRow.name, def: itemTagRow.def }}
				cardsById={defsById}
				archived={collection?.status === 'archived'}
				onChanged={() => onItemTagsChanged?.()}
			/>
		) : null }

		<Modal
			open={pickerOpen}
			title='Add card'
			onCancel={() => setPickerOpen( false )}
			destroyOnClose
			footer={<Button type='primary' onClick={() => setPickerOpen( false )}>Done</Button>}
			className='card-library__picker'
		>
			<Input
				{...NO_TEXT_ASSIST}
				allowClear
				prefix={<MAGNIFYING_GLASS aria-hidden='true' />}
				placeholder='Search cards…'
				aria-label='Search cards to add'
				value={search}
				onChange={e => setSearch( e.target.value )}
			/>
			{ available.length === 0 ? (
				<div className='card-library__empty'>Every card is already in this collection.</div>
			) : searchResults.length === 0 ? (
				noMatches( 'cards to add' )
			) : (
				<div className='card-library__grid card-library__picker-list'>
					{ searchResults.map( cd => (
						<RecordIdentity
							key={normalizeId( cd._id )}
							className='card-library__row'
							badge={<RecordTitle kind='card' record={cd} title={cd?.name || '(untitled card)'} wrap />}
							below={cd?.description ? <span>{cd.description}</span> : null}
							actions={(
								<Button aria-label={`Add ${ cd?.name || 'card' } to this collection`} onClick={() => addCard( cd )}>
									Add
								</Button>
							)}
						/>
					) ) }
				</div>
			) }
		</Modal>

		{ newSubOpen ? (
			<NewSubCollectionModal open={newSubOpen} onClose={() => setNewSubOpen( false )} parent={collection} />
		) : null }

		<ConfirmWordModal
			open={!! deletingRow}
			word='delete'
			title='Delete this card?'
			okText='Delete'
			busy={deleteBusy}
			onOk={confirmDeleteOwnedCard}
			onCancel={() => setDeletingRow( null )}
		>
			<p>
				Deleting <strong>{ ( deletingRow?.name || '' ).trim() || 'this card' }</strong> leaves existing items with their data,
				but no new item can use the card. You can restore it from Recently deleted.
			</p>
		</ConfirmWordModal>

		{/* Two links to the same card, and they are not the same thing: the
		    direct one needs an account and this collection's permissions, the
		    temporary one needs neither (managers only — the section gates
		    itself). */}
		<ShareModal
			open={!! shareRow}
			onClose={() => setShareRow( null )}
			modalTitle='Share card'
			preview={(
				<RecordIdentity
					kicker='Card definition'
					badge={(
						<RecordBadge
							kind='card'
							record={shareRow?.def || { name: shareRow?.name }}
							title={shareRow?.name || 'Card'}
						/>
					)}
				/>
			)}
			shareUrl={buildCardShareUrl( shareRow?.id )}
			downloadFileName={`keepr-card-${ slugify( shareRow?.name || '' ) || shareRow?.id || 'card' }`}
			className='card-library__share-modal'
			extra={(
				<TempShareLinkSection
					collectionId={collectionId}
					collection={collection}
					kind='card'
					cardId={shareRow?.id}
				/>
			)}
		/>
	</>;
}
